/**
 * Turn a dataAPI response carrying a file body into a browser download.
 *
 * Roughly ten screens have their own copy of this ~25-line block, and every one
 * of them reads the filename as `res.headers['Content-Disposition']`. `headers`
 * is a fetch `Headers` object, so bracket access always returns `undefined` and
 * every export in the app silently falls back to its hardcoded filename. This
 * uses `headers.get(...)`, which actually works — so the server-chosen name
 * (including its timestamp) survives.
 *
 * Usage:
 *   const res = await dataAPI(GET_ONE, resource, { urlSuffix: 'export/' });
 *   downloadBlobResponse(res, 'users.csv');
 */

const FILENAME_RE = /filename\*=UTF-8''([^;]+)|filename[^;=\n]*=((['"]).*?\3|[^;\n]*)/i;

export const filenameFromResponse = (res, fallback) => {
    const headers = res && res.headers;
    const disposition = headers && typeof headers.get === 'function'
        ? headers.get('Content-Disposition')
        : null;
    if (disposition && disposition.indexOf('attachment') !== -1) {
        const matches = FILENAME_RE.exec(disposition);
        if (matches) {
            // Prefer the RFC 5987 form when present: it is the one that
            // survives non-ASCII names.
            const raw = matches[1] ? decodeURIComponent(matches[1]) : matches[2];
            const cleaned = (raw || '').replace(/["']/g, '').trim();
            if (cleaned) return cleaned;
        }
    }
    return fallback;
};

/**
 * Start a browser download of a URL that answers with
 * `Content-Disposition: attachment` — the background-task artifact links do,
 * since the backend signs them that way (CP-8361).
 *
 * A hidden iframe rather than an anchor click or `location.assign`: the URL
 * is cross-origin (S3), so `a.download` is ignored and the response decides.
 * With an attachment header the download starts and the page is untouched;
 * if S3 ever answered with an error page instead, it would render invisibly
 * in the frame rather than replace the SPA. Verified in headless Chrome
 * without a user gesture, which is the situation here — the task finishes
 * on a poll, seconds after the click.
 */
export const downloadFromUrl = (url) => {
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    frame.setAttribute('aria-hidden', 'true');
    frame.src = url;
    document.body.appendChild(frame);
    // Once the browser has the response headers the download is its own; the
    // frame only has to outlive the round trip to S3.
    setTimeout(() => {
        if (frame.parentNode) frame.parentNode.removeChild(frame);
    }, 60000);
};

const downloadBlobResponse = async (res, fallbackFilename = 'download') => {
    const body = await res.response.blob();
    const url = window.URL.createObjectURL(body);
    const filename = filenameFromResponse(res, fallbackFilename);

    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    setTimeout(() => {
        document.body.removeChild(a);
    }, 100);
    return filename;
};

export default downloadBlobResponse;
